const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

const DEFAULTS = {
  targetRatio: 0.32,
  maxLiftRatio: 0.4,
  liftStartRatio: 0.12
}

const computeLiftSpec = (faceOvalPoints, browPoints, options = {}) => {
  if (!faceOvalPoints?.length) return null
  let browMinY = Infinity
  if (Number.isFinite(options.browMinYOverride)) {
    browMinY = options.browMinYOverride
  } else if (browPoints?.length) {
    for (let i = 0; i < browPoints.length; i += 1) {
      const p = browPoints[i]
      if (!p) continue
      if (p.y < browMinY) browMinY = p.y
    }
  }
  let faceTopY = Infinity
  let chinY = -Infinity
  for (let i = 0; i < faceOvalPoints.length; i += 1) {
    const p = faceOvalPoints[i]
    if (!p) continue
    if (p.y < faceTopY) faceTopY = p.y
    if (p.y > chinY) chinY = p.y
  }
  if (!Number.isFinite(browMinY) || !Number.isFinite(faceTopY) || !Number.isFinite(chinY))
    return null
  const browToChin = chinY - browMinY
  if (!(browToChin > 0)) return null

  const targetRatio = Number.isFinite(options.targetRatio) ? options.targetRatio : DEFAULTS.targetRatio
  const maxLiftRatio = Number.isFinite(options.maxLiftRatio)
    ? options.maxLiftRatio
    : DEFAULTS.maxLiftRatio
  const liftStartRatio = Number.isFinite(options.liftStartRatio)
    ? options.liftStartRatio
    : DEFAULTS.liftStartRatio

  const targetTopY = browMinY - browToChin * targetRatio
  const maxLift = browToChin * maxLiftRatio
  const lift = Math.min(Math.max(0, faceTopY - targetTopY), maxLift)
  if (lift < 1) return null

  const liftStartY = browMinY + browToChin * liftStartRatio
  const liftRange = Math.max(1, liftStartY - faceTopY)
  return { lift, liftStartY, liftRange }
}

export const expandFaceOvalPoints = (faceOvalPoints, browPoints, options = {}) => {
  const spec = computeLiftSpec(faceOvalPoints, browPoints, options)
  if (!spec) return faceOvalPoints
  const { lift, liftStartY, liftRange } = spec
  return faceOvalPoints.map((p) => {
    if (!p || p.y > liftStartY) return p
    const w = clamp((liftStartY - p.y) / liftRange, 0, 1)
    return { ...p, y: Math.max(0, p.y - lift * w) }
  })
}

export const expandFaceOvalPointsByIndices = (pointsPx, contourPaths, options = {}) => {
  if (!pointsPx?.length || !contourPaths) return pointsPx
  const faceOval = contourPaths.faceOval || []
  const browIdx = [
    ...(contourPaths.leftBrow || []),
    ...(contourPaths.rightBrow || [])
  ].filter((v) => Number.isInteger(v))
  if (!faceOval.length || !browIdx.length) return pointsPx

  const browPoints = browIdx.map((idx) => pointsPx[idx]).filter(Boolean)
  const faceOvalPoints = faceOval.map((idx) => pointsPx[idx]).filter(Boolean)
  const spec = computeLiftSpec(faceOvalPoints, browPoints, options)
  if (!spec) return pointsPx
  const { lift, liftStartY, liftRange } = spec

  const out = pointsPx.map((p) => (p ? { ...p } : p))
  for (let i = 0; i < faceOval.length; i += 1) {
    const idx = faceOval[i]
    const p = out[idx]
    if (!p || p.y > liftStartY) continue
    const w = clamp((liftStartY - p.y) / liftRange, 0, 1)
    p.y = Math.max(0, p.y - lift * w)
  }
  return out
}

export const computeBrowMinY = (points, contourPaths) => {
  if (!points?.length || !contourPaths) return null
  const browIdx = [
    ...(contourPaths.leftBrow || []),
    ...(contourPaths.rightBrow || [])
  ].filter((v) => Number.isInteger(v))
  if (!browIdx.length) return null
  let browMinY = Infinity
  for (let i = 0; i < browIdx.length; i += 1) {
    const p = points[browIdx[i]]
    if (!p) continue
    if (p.y < browMinY) browMinY = p.y
  }
  return Number.isFinite(browMinY) ? browMinY : null
}

export const captureForeheadEdgeBackup = (points, contourPaths, options = {}) => {
  if (!points?.length || !contourPaths) return null
  const faceOval = contourPaths.faceOval || []
  const browIdx = [
    ...(contourPaths.leftBrow || []),
    ...(contourPaths.rightBrow || [])
  ].filter((v) => Number.isInteger(v))
  if (!faceOval.length || !browIdx.length) return null

  let browMinY = Infinity
  for (let i = 0; i < browIdx.length; i += 1) {
    const p = points[browIdx[i]]
    if (!p) continue
    if (p.y < browMinY) browMinY = p.y
  }
  let chinY = -Infinity
  for (let i = 0; i < faceOval.length; i += 1) {
    const p = points[faceOval[i]]
    if (!p) continue
    if (p.y > chinY) chinY = p.y
  }
  if (!Number.isFinite(browMinY) || !Number.isFinite(chinY)) return null
  const browToChin = chinY - browMinY
  if (!(browToChin > 0)) return null

  const margin = Number.isFinite(options.margin)
    ? options.margin
    : Math.max(0.015, browToChin * 0.04)
  const lockY = browMinY - margin
  const backup = []
  for (let i = 0; i < faceOval.length; i += 1) {
    const idx = faceOval[i]
    const p = points[idx]
    if (!p || p.y > lockY) continue
    backup.push({ idx, x: p.x, y: p.y, z: p.z || 0 })
  }
  return backup.length ? backup : null
}

export const restoreForeheadEdgeBackup = (points, backup) => {
  if (!points?.length || !backup?.length) return
  for (let i = 0; i < backup.length; i += 1) {
    const b = backup[i]
    const p = points[b.idx]
    if (!p) continue
    p.x = b.x
    p.y = b.y
    p.z = b.z
  }
}

export default {
  expandFaceOvalPoints,
  expandFaceOvalPointsByIndices,
  computeBrowMinY,
  captureForeheadEdgeBackup,
  restoreForeheadEdgeBackup
}
